import { useCallback } from 'react'
import useIsomorphicLayoutEffect from './use-isomorphic-layout-effect'
import { InputType } from '../text-input'

/**
 * Inspired by focus-visible polyfill.
 */
export const useFocusVisiblePolyfill = () => {
  const applyFocusVisiblePolyfill = useCallback(() => {
    let hadKeyboardEvent = true

    const addFocusVisibleClass = (el: HTMLElement | null) => {
      if (el && !el.classList.contains('focus-visible')) {
        el.classList.add('focus-visible')
      }
    }

    const removeFocusVisibleClass = (el: HTMLElement | null) => {
      if (el && el.classList.contains('focus-visible')) {
        el.classList.remove('focus-visible')
      }
    }

    const elementShouldTriggerFocusVisible = (el: HTMLElement | null) => {
      if (
        el instanceof HTMLInputElement &&
        Object.values(InputType).includes(el.type as InputType)
      ) {
        return true
      }
      if (el instanceof HTMLTextAreaElement) {
        return true
      }
      return false
    }

    const onKeydown = (e: KeyboardEvent) => {
      if (e.metaKey || e.altKey || e.ctrlKey) {
        return
      }

      const el = e.target as HTMLElement | null

      // Shift key is a modifier key, but should trigger focus if it's a text input
      if (
        e.shiftKey &&
        el instanceof HTMLInputElement &&
        Object.values(InputType).includes(el.type as InputType)
      ) {
        return
      }

      addFocusVisibleClass(el)
      hadKeyboardEvent = true
    }

    const onMousedown = (_: MouseEvent) => {
      hadKeyboardEvent = false
    }

    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null
      if (hadKeyboardEvent || elementShouldTriggerFocusVisible(el)) {
        addFocusVisibleClass(el)
      }
    }

    const onBlur = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null
      removeFocusVisibleClass(el)
    }

    document.addEventListener('mousedown', onMousedown, true)
    document.addEventListener('keydown', onKeydown, true)
    document.addEventListener('focus', onFocus, true)
    document.addEventListener('blur', onBlur, true)

    return () => {
      document.removeEventListener('mousedown', onMousedown, true)
      document.removeEventListener('keydown', onKeydown, true)
      document.removeEventListener('focus', onFocus, true)
      document.removeEventListener('blur', onBlur, true)
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    const style = document.createElement('style')

    try {
      document.head.appendChild(style)
      style.sheet?.insertRule(':focus-visible { color: inherit }')
    } catch {
      return applyFocusVisiblePolyfill()
    } finally {
      style.remove()
    }
  }, [])
}
